import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import '../../../component-styles/Proposal-UI.css';

function formatCurrency(v) {
  return Number(v || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

const APPROVAL_DETAIL = {
  draft: "It's still a draft, so the client hasn't approved it.",
  sent: "The client hasn't approved it in InteriorFolio yet.",
  changes_requested: "The client asked for changes and hasn't approved it since.",
  archived: "It's archived, so it isn't approved.",
};

function describeBlocker(blocker) {
  if (blocker.code === 'not_approved') {
    return {
      title: 'Waiting on approval',
      detail: APPROVAL_DETAIL[blocker.status] || "It hasn't been approved yet.",
    };
  }
  if (blocker.code === 'deposit_not_received') {
    const required = formatCurrency(blocker.requiredDeposit);
    return {
      title: 'Deposit not recorded',
      detail:
        Number(blocker.depositReceived) > 0
          ? `This proposal asks for a ${required} deposit. ${formatCurrency(
              blocker.depositReceived
            )} has been recorded in InteriorFolio, leaving ${formatCurrency(
              blocker.depositOutstanding
            )}.`
          : `This proposal asks for a ${required} deposit, and no payment has been recorded in InteriorFolio yet.`,
    };
  }
  return { title: 'Not ready yet', detail: blocker.message || '' };
}

const FIXES = {
  mark_approved: {
    label: 'Mark as Approved',
    hint: 'The client approved some other way — by email, a signed copy, or by paying you in QuickBooks.',
  },
  send_to_client: {
    label: 'Send to Client',
    hint: 'Email it so the client can review and approve it online.',
  },
  record_payment: {
    label: 'Record Payment',
    hint: 'Enter what the client paid. If they paid you in QuickBooks, tick "Already in QuickBooks" so it isn\'t posted there twice.',
  },
};

// Shown instead of a dead-end error when a proposal can't become purchase
// orders yet: what's in the way, and a button for each way past it. Only fixes
// the caller passes a handler for are offered, so permissions decide the rest.
// Each handler gets the blocker it's fixing (e.g. how much deposit is owed).
export default function ConvertToPoBlockersModal({ proposalNumber, blockers, actions, onClose }) {
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, busy]);

  async function run(fix, blocker) {
    if (busy) return;
    setBusy(fix);
    try {
      await actions[fix](blocker);
    } finally {
      setBusy(null);
    }
  }

  const count = blockers.length;

  return (
    <div className="proposal-lock-overlay" onClick={() => !busy && onClose()}>
      <div
        className="proposal-lock-modal convert-po-blockers-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="convert-po-blockers-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="convert-po-blockers-title">Not ready for purchase orders yet</h3>
        <p>
          {proposalNumber || 'This proposal'} needs {count === 1 ? 'one thing' : `${count} things`}{' '}
          sorted out before it can be converted.
        </p>

        {blockers.map((blocker) => {
          const { title, detail } = describeBlocker(blocker);
          const fixes = (blocker.fixes || []).filter((fix) => FIXES[fix] && actions[fix]);
          return (
            <section key={blocker.code} className="convert-po-blocker">
              <h4>{title}</h4>
              <p>{detail}</p>
              {fixes.map((fix) => (
                <div key={fix} className="convert-po-fix">
                  <button
                    type="button"
                    className="if-btn-secondary"
                    disabled={Boolean(busy)}
                    onClick={() => run(fix, blocker)}
                  >
                    {busy === fix ? 'Working…' : FIXES[fix].label}
                  </button>
                  <span>{FIXES[fix].hint}</span>
                </div>
              ))}
            </section>
          );
        })}

        <div className="proposal-lock-actions">
          <button
            type="button"
            className="if-btn-secondary"
            onClick={onClose}
            disabled={Boolean(busy)}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

ConvertToPoBlockersModal.propTypes = {
  proposalNumber: PropTypes.string,
  blockers: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      message: PropTypes.string,
      status: PropTypes.string,
      requiredDeposit: PropTypes.number,
      depositReceived: PropTypes.number,
      depositOutstanding: PropTypes.number,
      fixes: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  onClose: PropTypes.func.isRequired,
};
